// Jobs.js - Main jobs dashboard component
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { Box, Typography, TablePagination, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField, InputAdornment } from '@mui/material'; // Import TablePagination + blast dialog components
import JobsTable from '../../components/JobsTable';
import { blastJob as blastJobService, stopBlastJob as stopBlastJobService, sendCardPayment as sendCardPaymentService } from '../../services/jobDashboardService';
import { JobDialogManager, useJobDialogs } from '../../components/JobActionDialogs/JobDialogManager';
import useJobData from '../../hooks/useJobData';
import useVehicleData from '../../hooks/useVehicleData';
import { useLoading } from '../../context/LoadingContext'; // Import useLoading
import { filterJobs, getStatusPriority } from '../../utils/jobUtils';
import * as jobActionHandlers from '../../utils/jobActionHandlers';
import { handleCancelSPAssignment, handleFindNextOrg1Driver } from '../../utils/jobActionHandlers'; // Import the new handlers

function Jobs({ jobCategory, filters, onCreateJob, onReceiveDemoJob, onEditJob, refreshTrigger = 0, onSocketStatusChange, setNotification }) { // Add filters prop
  // Get the job data from our custom hook
  const {
    jobs,
    setJobs,
    drivers,
    loading: jobsLoading,
    // setLoading, // Removed - useJobData no longer returns it
    error: jobsError,
    currentUser,
    refreshJobs,
    deleteJob,
    page, // Destructure page
    setPage, // Destructure setPage
    rowsPerPage, // Destructure rowsPerPage
    setRowsPerPage, // Destructure setRowsPerPage
    totalCount // Destructure totalCount
  } = useJobData(jobCategory, filters, onSocketStatusChange); // Pass filters to useJobData
  const { startLoading } = useLoading(); // Get startLoading from context
  
  // Get fleet vehicles from our custom hook
  const { 
    fleetVehicles, 
    isLoading: vehiclesLoading, 
    error: vehiclesError 
  } = useVehicleData();
  
  // Combined loading and error states
  const loading = jobsLoading || vehiclesLoading;
  const error = jobsError || vehiclesError;
  
  // Debug: Log fleet vehicles
  useEffect(() => {
    // console.log('Fleet vehicles from useVehicleData:', fleetVehicles); // Removed log
  }, [fleetVehicles]);

  // Get dialogs state from our custom hook
  const dialogState = useJobDialogs();
  const {
    selectedJob, setSelectedJob,
    jobToReject, setJobToReject,
    jobToCancel, setJobToCancel,
    jobToMarkGOA, setJobToMarkGOA,
    jobToUpdateEta, setJobToUpdateEta,
    jobToMarkUnsuccessful, setJobToMarkUnsuccessful,
    etaDialogOpen, setEtaDialogOpen,
    dispatchDialogOpen, setDispatchDialogOpen,
    rejectDialogOpen, setRejectDialogOpen,
    cancelDialogOpen, setCancelDialogOpen,
    goaDialogOpen, setGoaDialogOpen,
    etaUpdateDialogOpen, setEtaUpdateDialogOpen,
    unsuccessfulDialogOpen, setUnsuccessfulDialogOpen,
    // Destructure the missing state setters for Org1 Driver ETA dialog
    setJobForOrg1DriverEtaDecision,
    setOrg1DriverEtaDecisionDialogOpen
  } = dialogState;

  // Predefined trucks list as fallback (only used if no fleet vehicles are available)
  const trucks = ['Flatbed 1', 'Flatbed 2', 'Wheel Lift 1', 'Heavy Duty 1', 'Service Truck 1'];

  // Handlers that leverage our utility functions
  const handleAcceptJob = useCallback((job) => {
    jobActionHandlers.handleAcceptJob(
      job, 
      setSelectedJob, 
      setEtaDialogOpen, 
      (job) => jobActionHandlers.handleDirectAccept(job, startLoading, refreshJobs) // Pass startLoading
    );
  }, [setSelectedJob, setEtaDialogOpen, startLoading, refreshJobs]); // Added startLoading dependency

  const handleRejectJob = useCallback((jobId) => {
    jobActionHandlers.handleRejectJob(
      jobId,
      jobs,
      setJobToReject,
      setRejectDialogOpen
    );
  }, [jobs, setJobToReject, setRejectDialogOpen]);

  const handleDispatchJob = useCallback((job) => {
    jobActionHandlers.handleDispatchJob(
      job,
      setSelectedJob,
      setDispatchDialogOpen
    );
  }, [setSelectedJob, setDispatchDialogOpen]);

  // --- Blast dispatch: broadcast the offer to all providers within a radius ---
  const [blastDialogOpen, setBlastDialogOpen] = useState(false);
  const [jobToBlast, setJobToBlast] = useState(null);
  const [blastRadius, setBlastRadius] = useState('25');
  const [blastSubmitting, setBlastSubmitting] = useState(false);

  const handleBlastJob = useCallback((job) => {
    setJobToBlast(job);
    setBlastRadius(job.blastDispatch?.initialRadiusMiles?.toString() || '25');
    setBlastDialogOpen(true);
  }, []);

  // --- Card payment for completed credit-card jobs ---
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [jobForCardPayment, setJobForCardPayment] = useState(null);
  const [cardForm, setCardForm] = useState({ cardNumber: '', expiry: '', cvv: '', cardholderName: '' });
  const [cardSubmitting, setCardSubmitting] = useState(false);

  const handleCardPayment = useCallback((job) => {
    setJobForCardPayment(job);
    setCardForm({ cardNumber: '', expiry: '', cvv: '', cardholderName: '' });
    setCardDialogOpen(true);
  }, []);

  const handleCardPaymentSubmit = useCallback(async () => {
    const digits = cardForm.cardNumber.replace(/\D/g, '');
    if (digits.length < 13 || !cardForm.expiry || !cardForm.cvv || !cardForm.cardholderName) {
      setNotification({ open: true, message: 'Fill in the full card details before sending.', severity: 'error' });
      return;
    }
    setCardSubmitting(true);
    const stopLoading = startLoading();
    try {
      const result = await sendCardPaymentService(jobForCardPayment.id, cardForm);
      setNotification({ open: true, message: result.message || 'Card payment sent.', severity: 'success' });
      setCardDialogOpen(false);
      setJobForCardPayment(null);
      refreshJobs();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send card payment. Please try again.';
      setNotification({ open: true, message, severity: 'error' });
    } finally {
      setCardSubmitting(false);
      stopLoading();
    }
  }, [cardForm, jobForCardPayment, setNotification, startLoading, refreshJobs]);

  const handleStopBlast = useCallback(async () => {
    setBlastSubmitting(true);
    const stopLoading = startLoading();
    try {
      const result = await stopBlastJobService(jobToBlast.id);
      setNotification({ open: true, message: result.message || 'Blast stopped.', severity: 'info' });
      setBlastDialogOpen(false);
      setJobToBlast(null);
      refreshJobs();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to stop blast dispatch. Please try again.';
      setNotification({ open: true, message, severity: 'error' });
    } finally {
      setBlastSubmitting(false);
      stopLoading();
    }
  }, [jobToBlast, setNotification, startLoading, refreshJobs]);

  const handleBlastConfirm = useCallback(async () => {
    const radius = parseFloat(blastRadius);
    if (isNaN(radius) || radius <= 0 || radius > 500) {
      setNotification({ open: true, message: 'Enter a radius between 1 and 500 miles.', severity: 'error' });
      return;
    }
    setBlastSubmitting(true);
    const stopLoading = startLoading();
    try {
      const result = await blastJobService(jobToBlast.id, radius);
      setNotification({ open: true, message: result.message || 'Blast sent.', severity: 'success' });
      setBlastDialogOpen(false);
      setJobToBlast(null);
      refreshJobs();
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to start blast dispatch. Please try again.';
      setNotification({ open: true, message, severity: 'error' });
    } finally {
      setBlastSubmitting(false);
      stopLoading();
    }
  }, [blastRadius, jobToBlast, setNotification, startLoading, refreshJobs]);

  const handleJobStatusChange = useCallback((jobId, status) => {
    console.log('Before status change - job expanded states:', jobs.map(job => ({
      id: job.id,
      expanded: job.expanded
    })));
    
    jobActionHandlers.handleJobStatusChange(
      jobId,
      status,
      jobs,
      currentUser,
      startLoading, // Pass startLoading
      // Pass a custom refreshJobs function that preserves expanded state
      () => {
        // console.log('Custom refreshJobs called - preserving expanded states'); // Removed log
        const expandedStates = {};
        // Save expanded states before refresh
        jobs.forEach(job => {
          expandedStates[job.id] = job.expanded;
        });
        
        // Call original refreshJobs
        return refreshJobs().then(() => {
          // After refresh, restore expanded states
          setJobs(currentJobs => {
            // console.log('Restoring expanded states after refresh'); // Removed log
            return currentJobs.map(job => {
              if (expandedStates.hasOwnProperty(job.id)) {
                return { ...job, expanded: expandedStates[job.id] };
              }
              return job;
            });
          });
        });
      },
      handleDispatchJob,
      setJobs,
      jobCategory,
      getStatusPriority,
      setNotification // Add missing setNotification parameter
    );
    
    // Do not refresh again - we already did it in the custom function
    // removed: refreshJobs();
  }, [jobs, currentUser, startLoading, refreshJobs, handleDispatchJob, setJobs, jobCategory, setNotification]); // Add setNotification to dependencies

  const handleExpandClick = useCallback((jobId) => {
    // console.log(`Jobs.js: handleExpandClick called for job ${jobId}`); // Removed log
    // console.log('Current jobs state before expand:', jobs.map(job => ({ // Removed log
    //   id: job.id, // Removed log
    //   expanded: job.expanded // Removed log
    // }))); // Removed log
    
    jobActionHandlers.handleExpandClick(jobId, setJobs);
    
    // We can't log the updated state here because setState is asynchronous
    // The updated state will be visible in the next render
  }, [setJobs, jobs]);

  const handleMenuAction = useCallback((action, job, user = currentUser) => {
    // Create custom refreshJobs function to preserve expanded states
    const preservingRefreshJobs = () => {
      // console.log('preservingRefreshJobs called from handleMenuAction'); // Removed log
      const expandedStates = {};
      // Save expanded states before refresh
      jobs.forEach(job => {
        expandedStates[job.id] = job.expanded;
      });
      
      // Call original refreshJobs
      return refreshJobs().then(() => {
        // After refresh, restore expanded states
        setJobs(currentJobs => {
          // console.log('Restoring expanded states after menu action'); // Removed log
          return currentJobs.map(job => {
            if (expandedStates.hasOwnProperty(job.id)) {
              return { ...job, expanded: expandedStates[job.id] };
            }
            return job;
          });
        });
      });
    };
    
  jobActionHandlers.handleMenuAction(
      action,
      job,
      setJobToCancel,
      setCancelDialogOpen,
      setJobToMarkGOA,
      setGoaDialogOpen,
      setJobToUpdateEta,
      setEtaUpdateDialogOpen,
      setJobToMarkUnsuccessful,
      setUnsuccessfulDialogOpen,
      handleDispatchJob,
      preservingRefreshJobs, // Use our custom wrapper
      startLoading, // Pass startLoading
      user, // Pass the user parameter
      setNotification // Pass setNotification
    );
  }, [
    jobs, // Added jobs to the dependency array since we use it in the callback
    currentUser, // Added currentUser since we use it as a default parameter
    setJobToCancel,
    setCancelDialogOpen,
    setJobToMarkGOA,
    setGoaDialogOpen,
    setJobToUpdateEta,
    setEtaUpdateDialogOpen,
    setJobToMarkUnsuccessful,
    setUnsuccessfulDialogOpen,
    handleDispatchJob,
    refreshJobs,
    startLoading, // Added startLoading dependency
    setJobs // Added setJobs since we use it in the callback
  ]);

  // Handler for the new SP cancellation action
  const handleCancelSPAssignmentAction = useCallback((jobId) => {
    // Call the handler from jobActionHandlers, passing necessary dependencies including setJobs and setNotification
    handleCancelSPAssignment(jobId, startLoading, refreshJobs, setJobs, setNotification);
  }, [startLoading, refreshJobs, setJobs, setNotification]); // Add setNotification dependency

  // Handler for finding the next Org 1 driver
  const handleFindNextOrg1DriverAction = useCallback((jobId) => {
    // Call the handler from jobActionHandlers, passing setNotification
    handleFindNextOrg1Driver(jobId, startLoading, refreshJobs, setJobs, setNotification);
  }, [startLoading, refreshJobs, setJobs, setNotification]); // Add setNotification to dependencies
  
  // Listen for dispatchJob events from JobDetails component
  useEffect(() => {
    const handleDispatchJobEvent = (event) => {
      const jobId = event.detail.jobId;
      const job = jobs.find(j => j.id === jobId);
      if (job) {
        handleDispatchJob(job);
      }
    };
    
    window.addEventListener('dispatchJob', handleDispatchJobEvent);
    
    return () => {
      window.removeEventListener('dispatchJob', handleDispatchJobEvent);
    };
  }, [jobs, handleDispatchJob]);

  // Get filtered jobs based on category and user role - using useMemo to avoid unnecessary recalculations
  const displayedJobs = useMemo(() => {
    return filterJobs(jobs, jobCategory, currentUser);
  }, [jobs, jobCategory, currentUser]);

  // Check if user data is fully ready - check both id and _id (MongoDB format)
  const isUserDataReady = Boolean(currentUser && (currentUser.id || currentUser._id));
  
  // For debugging: log user data structure if not considered ready
  if (currentUser && !isUserDataReady) {
    // console.log('User data not considered ready:', { // Removed log
    //   hasId: Boolean(currentUser.id), // Removed log
    //   has_Id: Boolean(currentUser._id), // Removed log
    //   keys: Object.keys(currentUser) // Removed log
    // }); // Removed log
  }
  
  // Add a state to force render after timeout if jobs exist but user data check is failing
  const [forceRender, setForceRender] = useState(false);
  
  // Effect to prevent infinite loading by forcing render after a timeout
  useEffect(() => {
    let timer;
    if (!isUserDataReady && jobs.length > 0 && !forceRender) {
      timer = setTimeout(() => {
        // console.log('Forcing render after timeout - jobs exist but user data not ready'); // Removed log
        setForceRender(true);
      }, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isUserDataReady, jobs.length, forceRender]);
  
  // Final loading state - won't be stuck if we have jobs but user data is problematic
  const isLoading = (loading || (!isUserDataReady && !forceRender));

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  return (
    <Box mt={2}>
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center" my={4}>
          <Typography>Loading jobs data...</Typography>
        </Box>
      )}
      {error && (
        <Typography color="error" variant="body1">
          {error}
        </Typography>
      )}

      {displayedJobs.length > 0 ? (
        <>
          <JobsTable 
            jobs={displayedJobs}
            currentUser={currentUser}
            jobCategory={jobCategory}
            loading={isLoading}
            onExpandClick={handleExpandClick}
            onEditJob={onEditJob}
            onAcceptJob={handleAcceptJob}
            onRejectJob={handleRejectJob}
            onDispatchJob={handleDispatchJob}
            onBlastJob={handleBlastJob}
            onCardPayment={handleCardPayment}
            onJobStatusChange={handleJobStatusChange}
            onMenuAction={handleMenuAction}
            onDeleteJob={deleteJob}
            onCancelSPAssignment={handleCancelSPAssignmentAction} // Pass the SP cancellation handler
            onFindNextOrg1Driver={handleFindNextOrg1DriverAction} // Pass the new Org 1 driver handler
            setJobs={setJobs} // Pass setJobs down
            refreshJobs={refreshJobs} // Pass refreshJobs down
            setNotification={setNotification} // Pass setNotification down
            // Removed unused Org1 Driver ETA dialog setters
          />
          
          {/* Dialog manager handles all the job-related dialogs */}
          <JobDialogManager
            loading={loading}
            currentUser={currentUser} // Pass currentUser here
            selectedJob={selectedJob}
            setSelectedJob={setSelectedJob}
            refreshJobs={refreshJobs}
            startLoading={startLoading} // Pass startLoading down
            drivers={drivers}
            trucks={trucks}
            vehicles={fleetVehicles}
            jobToReject={jobToReject}
            setJobToReject={setJobToReject}
            jobToCancel={jobToCancel}
            setJobToCancel={setJobToCancel}
            jobToMarkGOA={jobToMarkGOA}
            setJobToMarkGOA={setJobToMarkGOA}
            jobToUpdateEta={jobToUpdateEta}
            setJobToUpdateEta={setJobToUpdateEta}
            jobToMarkUnsuccessful={jobToMarkUnsuccessful}
            setJobToMarkUnsuccessful={setJobToMarkUnsuccessful}
            etaDialogOpen={etaDialogOpen}
            setEtaDialogOpen={setEtaDialogOpen}
            dispatchDialogOpen={dispatchDialogOpen}
            setDispatchDialogOpen={setDispatchDialogOpen}
            rejectDialogOpen={rejectDialogOpen}
            setRejectDialogOpen={setRejectDialogOpen}
            cancelDialogOpen={cancelDialogOpen}
            setCancelDialogOpen={setCancelDialogOpen}
            goaDialogOpen={goaDialogOpen}
            setGoaDialogOpen={setGoaDialogOpen}
            etaUpdateDialogOpen={etaUpdateDialogOpen}
            setEtaUpdateDialogOpen={setEtaUpdateDialogOpen}
            unsuccessfulDialogOpen={unsuccessfulDialogOpen}
            setUnsuccessfulDialogOpen={setUnsuccessfulDialogOpen}
            setNotification={setNotification} // Pass setNotification
          />
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]} // Standard options
          />

          {/* Card payment dialog: temporary card details emailed to the provider */}
          <Dialog open={cardDialogOpen} onClose={() => !cardSubmitting && setCardDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Send Card Payment{jobForCardPayment?.po ? ` — Job ${jobForCardPayment.po}` : ''}</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Enter the temporary credit card for {jobForCardPayment?.driver || 'the provider'} to collect
                {jobForCardPayment?.invoice?.total ? ` $${Number(jobForCardPayment.invoice.total).toFixed(2)}` : ' payment'}.
                The details are emailed to the provider and are not stored (only the last 4 digits are kept for records).
              </DialogContentText>
              <TextField fullWidth margin="dense" label="Cardholder Name" value={cardForm.cardholderName}
                onChange={(e) => setCardForm(prev => ({ ...prev, cardholderName: e.target.value }))} autoFocus />
              <TextField fullWidth margin="dense" label="Card Number" value={cardForm.cardNumber}
                onChange={(e) => setCardForm(prev => ({ ...prev, cardNumber: e.target.value.replace(/[^\d ]/g, '') }))}
                inputProps={{ inputMode: 'numeric', maxLength: 23 }} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField margin="dense" label="Expiry (MM/YY)" value={cardForm.expiry}
                  onChange={(e) => setCardForm(prev => ({ ...prev, expiry: e.target.value }))} inputProps={{ maxLength: 7 }} sx={{ flex: 1 }} />
                <TextField margin="dense" label="CVV" value={cardForm.cvv}
                  onChange={(e) => setCardForm(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '') }))}
                  inputProps={{ inputMode: 'numeric', maxLength: 4 }} sx={{ flex: 1 }} />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setCardDialogOpen(false)} disabled={cardSubmitting}>Cancel</Button>
              <Button onClick={handleCardPaymentSubmit} variant="contained" color="primary" disabled={cardSubmitting}>
                {cardSubmitting ? 'Sending...' : 'Email Card to Provider'}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Blast Dispatch dialog */}
          <Dialog open={blastDialogOpen} onClose={() => !blastSubmitting && setBlastDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Blast Dispatch{jobToBlast?.po ? ` — Job ${jobToBlast.po}` : ''}</DialogTitle>
            <DialogContent>
              {jobToBlast?.status === 'Blast Dispatch' && jobToBlast?.blastDispatch?.active && (
                <DialogContentText sx={{ mb: 2, color: 'warning.main' }}>
                  A blast is currently running: round {jobToBlast.blastDispatch.round || 1} of {jobToBlast.blastDispatch.maxRounds || 2},
                  radius {jobToBlast.blastDispatch.radiusMiles} miles,
                  offered to {(jobToBlast.blastDispatch.vendorNumbers || []).length} SP(s) and {(jobToBlast.blastDispatch.driverIds || []).length} driver(s).
                  You can stop it or restart it with a new radius.
                </DialogContentText>
              )}
              {jobToBlast?.blastDispatch?.failed && (
                <DialogContentText sx={{ mb: 2, color: 'error.main' }}>
                  The previous blast failed after {jobToBlast.blastDispatch.round || 1} round(s) with no acceptance
                  within {jobToBlast.blastDispatch.radiusMiles} miles. Sending a new blast restarts from the radius below.
                </DialogContentText>
              )}
              <DialogContentText sx={{ mb: 2 }}>
                Send this job to every service provider facility and on-duty driver within the
                radius below. The first one to accept wins the job. If nobody accepts, the blast
                repeats once with a larger radius, then stops and notifies dispatch for a decision.
              </DialogContentText>
              <TextField
                autoFocus
                fullWidth
                label="Radius"
                type="number"
                value={blastRadius}
                onChange={(e) => setBlastRadius(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">miles</InputAdornment>,
                  inputProps: { min: 1, max: 500 }
                }}
                helperText="1 - 500 miles. Radius grows by 25 miles on the second round."
              />
            </DialogContent>
            <DialogActions>
              {(jobToBlast?.blastDispatch?.active || jobToBlast?.blastDispatch?.failed) && (
                <Button onClick={handleStopBlast} color="error" disabled={blastSubmitting} sx={{ mr: 'auto' }}>
                  Stop Blast
                </Button>
              )}
              <Button onClick={() => setBlastDialogOpen(false)} disabled={blastSubmitting}>Cancel</Button>
              <Button onClick={handleBlastConfirm} variant="contained" color="primary" disabled={blastSubmitting}>
                {blastSubmitting ? 'Sending...' : jobToBlast?.status === 'Blast Dispatch' ? 'Re-Blast' : 'Send Blast'}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <Box display="flex" justifyContent="center" my={4}>
          <Typography>No jobs found for this category.</Typography>
        </Box>
      )}
    </Box>
  );
}

export default Jobs;
